/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Form, Button, Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import '../scss/frontend/consent-modal.scss';
import { SlArrowRight, SlArrowLeft } from 'react-icons/sl';

import {
  TransactionType,
  ConsentAgreementFormDataType,
} from '../model/consent.data-type';

import {
  GetUserConsent,
  SetUserConsentAgreement,
  GetOneTrustPurposesById
} from '../services/ApiConsent';

import { callMsGraph } from '../graph';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { graphConfig } from '../authConfig';

export const ConsentPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [activePurposes, setActivePurposes] = useState<any[] | []>([]);
  const [authUser, setAuthUser] = useState<any>();
  const [isFormUpdated, setIsFormUpdated] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [tabKey, setTabKey] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [isPurposesLoaded, setIsPurposesLoaded] = useState(false);

  // Function to get user info from Graph API
  const getUserFromGraph = async () => {
    const account = accounts[0];
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        scopes: graphConfig.scopes,
        account: account,
      });
      
      const userProfile = await callMsGraph(response.accessToken);
      return {
        email: userProfile?.mail || userProfile?.userPrincipalName,
        displayName: userProfile?.displayName,
        givenName: userProfile?.givenName,
        surname: userProfile?.surname,
        employeeNameFirst: userProfile?.givenName,
        employeeNameLast: userProfile?.surname,
      };
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        instance.acquireTokenRedirect({
          scopes: graphConfig.scopes,
          account: account,
        });
      }
      console.error('Error getting user from Graph:', error);
      return null;
    }
  };

  // Async loading with Promise.allSettled for optimal performance
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        
        // Start user loading immediately
        const userPromise = getUserFromGraph();
        
        // Get user first
        const infoUser = await userPromise;
        setAuthUser(infoUser);
        setIsUserLoaded(true);
        
        // If no user, stop loading
        if (!infoUser?.email) {
          setIsPurposesLoaded(true);
          setIsLoading(false);
          return;
        }
        
        // Load purposes and consent data in parallel using Promise.allSettled
        const dataPromises = [
          GetOneTrustPurposesById(),
          GetUserConsent({ idOrEmail: infoUser.email })
        ];
        
        const results = await Promise.allSettled(dataPromises);
        
        // Handle purposes result
        const activePurposesRes = results[0].status === 'fulfilled' 
          ? results[0].value 
          : [];
        
        // Handle user consent result  
        const userPurposesAgreement = results[1].status === 'fulfilled' 
          ? results[1].value 
          : null;
        
        // Process purposes data
        let processedPurposes = activePurposesRes || [];
        
        if (userPurposesAgreement?.content?.length) {
          setIsUpdate(true);
          const userPurposes = userPurposesAgreement.content[0]?.Purposes || [];
          
          // Merge OneTrust response with user agreement
          processedPurposes = activePurposesRes?.map((p: any) => {
            const isExisting = userPurposes.find((up: any) => up.Id === p.purposeId);
            let userAgreement = TransactionType.new;
            
            if (isExisting) {
              userAgreement = isExisting.Status === 'ACTIVE' 
                ? TransactionType.accept 
                : TransactionType.decline;
            }
            
            // Check version mismatch
            if (p?.version !== isExisting?.Version) {
              userAgreement = TransactionType.new;
            }
            
            return { ...p, userAgreement };
          }) || [];
        } else {
          setIsFormUpdated(true);
        }
        
        // Sort and set purposes
        if (processedPurposes.length > 0) {
          setActivePurposes(processedPurposes);
        }
        
        // Log any failed promises (optional - can be removed in production)
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const apiName = index === 0 ? 'GetOneTrustPurposesById' : 'GetUserConsent';
            console.warn(`${apiName} failed:`, result.reason);
          }
        });
        
      } catch (error) {
        console.error('ConsentPage async loading error:', error);
      } finally {
        setIsPurposesLoaded(true);
        setIsLoading(false);
      }
    })();
  }, []); // เพิ่ม dependency array ว่าง เพื่อให้รันครั้งเดียวเมื่อ component mount

  // Cache language setting to avoid repeated localStorage calls
  const [currentLang, setCurrentLang] = useState(() => localStorage.getItem('Language') || 'th');
  const isThLang = currentLang === 'th';
  
  const switchLang = useCallback((lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('Language', lang);
    setCurrentLang(lang);
  }, [i18n]);

  const { register, handleSubmit, watch } = useForm();
  
  // Memoize heavy operations
  const sortedActivePurposes = useMemo(() => {
    return activePurposes.length > 0 
      ? [...activePurposes].sort((a: any, b: any) => (a.sort || 0) - (b.sort || 0))
      : [];
  }, [activePurposes]);
  const onSubmit = ({ purposes }: any) => {
    (async () => {
      try {
        // แสดง toast loading
        toast.info('กำลังส่งข้อมูล...', {
          position: "top-center",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        });

        const formData: ConsentAgreementFormDataType = {
          identifier: authUser?.email,
          purposes: purposes,
          dsDataElements: {
            FirstName: authUser?.employeeNameFirst || '',
            LastName: authUser?.employeeNameLast || '',
          },
        };

        const response = await SetUserConsentAgreement(formData);

        if (response?.receipt) {
          // ปิด toast loading และแสดง success
          toast.dismiss();
          toast.success('ส่งข้อมูลสำเร็จ!', {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });

          // กลับไป Tab 1 ทันที
          setTabKey(1);
        }
      } catch (error) {
        // ปิด toast loading และแสดง error
        toast.dismiss();
        toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง!', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong! Please try again later!',
        });
      }
    })();
  };

  const watchForm = watch();

  // show hide submit button - optimized with useMemo to reduce re-renders
  useEffect(() => {
    if (watchForm?.purposes?.length && isUpdate) {
      const wps = watchForm?.purposes || [];

      for (const wp of wps) {
        const currentPurpose: any = sortedActivePurposes?.find(
          (ap) => ap?.purposeId === wp?.Id,
        );

        const updated =
          currentPurpose?.userAgreement !== wp?.TransactionType ? true : false;

        setIsFormUpdated(updated);

        if (updated) {
          break;
        }
      }
    }
  }, [watchForm, sortedActivePurposes, isUpdate]);

  // Show loading state during async operations
  if (isLoading || !isUserLoaded) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#666'
      }}>
        Loading consent application...
      </div>
    );
  }

  // Show partial UI with loading state for purposes if user is loaded but purposes aren't
  if (isUserLoaded && !isPurposesLoaded) {
    return (
      <div className="consent consent-page-wrapper" style={{ paddingLeft: 0, overflow: 'hidden', height: '100vh' }}>
        <Form style={{ backgroundColor: '#ffffff', height: '100vh', overflow: 'hidden' }}>
          <div className="modal-header" style={{ position: 'sticky', top: 0, background: 'white', zIndex: 999, padding: '1.5rem 2rem', textAlign: 'center', display: 'block', borderBottom: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <div className="modal-title">
              <div className="lang-switch-wrapper" style={{ marginBottom: '1rem' }}>
                <div style={{ 
                  display: 'inline-flex', 
                  background: '#f8f9fa', 
                  borderRadius: '25px', 
                  padding: '4px',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <Button
                    variant="outline-secondary"
                    className={`lang-switch-btn ${isThLang ? 'active' : ''}`}
                    onClick={() => switchLang('th')}
                    style={{
                      borderRadius: '20px',
                      padding: '8px 20px',
                      margin: '0 2px',
                      border: 'none',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      minWidth: '50px',
                      transition: 'all 0.3s ease',
                      backgroundColor: isThLang ? '#6c63ff' : 'transparent',
                      color: isThLang ? 'white' : '#6c757d',
                      boxShadow: isThLang ? '0 2px 8px rgba(108, 99, 255, 0.3)' : 'none'
                    }}
                  >
                    ไทย
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className={`lang-switch-btn ${!isThLang ? 'active' : ''}`}
                    onClick={() => switchLang('en')}
                    style={{
                      borderRadius: '20px',
                      padding: '8px 20px',
                      margin: '0 2px',
                      border: 'none',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      minWidth: '50px',
                      transition: 'all 0.3s ease',
                      backgroundColor: !isThLang ? '#6c63ff' : 'transparent',
                      color: !isThLang ? 'white' : '#6c757d',
                      boxShadow: !isThLang ? '0 2px 8px rgba(108, 99, 255, 0.3)' : 'none'
                    }}
                  >
                    ENG
                  </Button>
                </div>
              </div>
              <span className="title" style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#2c3e50',
                display: 'block',
                marginTop: '0.5rem'
              }}>
                {t('consent.user_agreement.modal_title')}
              </span>
            </div>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: 'calc(100vh - 200px)',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#666'
          }}>
            Finalizing consent data...
          </div>
        </Form>
      </div>
    );
  }



  return (
    <div className="consent consent-page-wrapper" style={{ paddingLeft: 0, overflow: 'hidden', height: '100vh' }}>
      <Form onSubmit={handleSubmit(onSubmit)} style={{ backgroundColor: '#ffffff', height: '100vh', overflow: 'hidden' }}>
        <div className="modal-header" style={{ position: 'sticky', top: 0, background: 'white', zIndex: 999, padding: '1.5rem 2rem', textAlign: 'center', display: 'block', borderBottom: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <div className="modal-title">
            <div className="lang-switch-wrapper" style={{ marginBottom: '1rem' }}>
              <div style={{ 
                display: 'inline-flex', 
                background: '#f8f9fa', 
                borderRadius: '25px', 
                padding: '4px',
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <Button
                  variant="outline-secondary"
                  className={`lang-switch-btn ${isThLang ? 'active' : ''}`}
                  onClick={() => switchLang('th')}
                  style={{
                    borderRadius: '20px',
                    padding: '8px 20px',
                    margin: '0 2px',
                    border: 'none',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    minWidth: '50px',
                    transition: 'all 0.3s ease',
                    backgroundColor: isThLang ? '#6c63ff' : 'transparent',
                    color: isThLang ? 'white' : '#6c757d',
                    boxShadow: isThLang ? '0 2px 8px rgba(108, 99, 255, 0.3)' : 'none'
                  }}
                >
                  ไทย
                </Button>
                <Button
                  variant="outline-secondary"
                  className={`lang-switch-btn ${!isThLang ? 'active' : ''}`}
                  onClick={() => switchLang('en')}
                  style={{
                    borderRadius: '20px',
                    padding: '8px 20px',
                    margin: '0 2px',
                    border: 'none',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    minWidth: '50px',
                    transition: 'all 0.3s ease',
                    backgroundColor: !isThLang ? '#6c63ff' : 'transparent',
                    color: !isThLang ? 'white' : '#6c757d',
                    boxShadow: !isThLang ? '0 2px 8px rgba(108, 99, 255, 0.3)' : 'none'
                  }}
                >
                  ENG
                </Button>
              </div>
            </div>
            <span className="title" style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#2c3e50',
              display: 'block',
              marginTop: '0.5rem'
            }}>
              {t('consent.user_agreement.modal_title')}
            </span>
          </div>
        </div>
        
        <div className="consent-modal-body" style={{ padding: 0, height: 'calc(100vh - 160px)', overflow: 'hidden' }}>
          <Tabs defaultActiveKey={1} activeKey={tabKey} fill>
            <Tab eventKey={1} title="" tabClassName="d-none">
              <div className="description" style={{ height: 'calc(100vh - 160px)', overflow: 'hidden' }}>
                {isThLang ? (
                  <iframe
                    title="Privacy Notice"
                    src="/one-trust/privacy-notice-th.html"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  ></iframe>
                ) : (
                  <iframe
                    title="Privacy Notice"
                    src="/one-trust/privacy-notice-en.html"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  ></iframe>
                )}
              </div>
            </Tab>
            <Tab eventKey={2} title="" tabClassName="d-none">
              <div className="purposes" style={{ margin: '1rem', fontSize: '14px' }}>
                {/* <div style={{ marginBottom: '1rem', color: 'blue', fontWeight: 'bold' }}>
                  Debug: Found {activePurposes?.length} purposes
                </div> */}
                {sortedActivePurposes?.map((activePurpose, i) => {

                  return (
                    <div key={i}>
                      <div className="purpose" style={{ paddingBottom: '12px' }}>
                        <input
                          type="hidden"
                          value={activePurpose?.purposeId}
                          {...register(`purposes.${i}.Id`)}
                        />
                        <input
                          type="hidden"
                          value={activePurpose?.version}
                          {...register(`purposes.${i}.Version`)}
                        />
                        <p style={{ color: 'rgb(105, 105, 105)', marginBottom: '6px' }}>
                          <span className="purpose-title" style={{ fontWeight: 'bold', marginRight: '6px' }}>
                            {(i18n.language === 'th') ? activePurpose?.labelTh : activePurpose?.labelEn}
                          </span>
                          <br />
                          <span style={{ marginRight: '6px' }}>{(i18n.language === 'th') ? activePurpose?.descriptionTh : activePurpose?.descriptionEn}</span>
                        </p>

                        <Form.Check
                          inline
                          label={t('accept')}
                          type="radio"
                          required
                          value={TransactionType.accept}
                          defaultChecked={
                            activePurpose?.userAgreement ===
                              TransactionType.accept
                              ? true
                              : false
                          }
                          {...register(`purposes.${i}.TransactionType`)}
                        />
                        <Form.Check
                          inline
                          label={t('decline')}
                          type="radio"
                          required
                          value={TransactionType.decline}
                          defaultChecked={
                            activePurpose?.userAgreement ===
                              TransactionType.decline
                              ? true
                              : false
                          }
                          {...register(`purposes.${i}.TransactionType`)}
                        />
                      </div>

                      {activePurposes?.length !== i + 1 && <hr />}
                    </div>
                  );
                })}
              </div>
            </Tab>
          </Tabs>
        </div>

        <div className="modal-footer" style={{ 
          position: 'sticky', 
          bottom: 0, 
          background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,1) 20%)', 
          zIndex: 999, 
          padding: '1.5rem 2rem',
          borderTop: '1px solid rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
        }}>
          {tabKey === 1 && (
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              fontWeight: 'bold'
            }}>
              <div style={{ 
                fontSize: '16px',
                color: '#495057',
                flex: '1'
              }}>
                {t('consent.user_agreement.first_tab_btn_label')}
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button
                  variant="outline-danger"
                  onClick={() => window.location.href = process.env.REACT_APP_PUBLIC_HR_PORTAL_URL || '/'}
                  style={{
                    borderRadius: '50px',
                    padding: '12px 24px',
                    fontWeight: 'bold',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <SlArrowLeft style={{ marginRight: '8px' }} />
                  {t('back') || 'กลับ'}
                </Button>
                <Button
                  variant="outline-secondary"
                  className="btn-light-green next-btn"
                  onClick={() => setTabKey(2)}
                  style={{
                    borderRadius: '50px',
                    padding: '12px 24px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(162, 177, 98, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <SlArrowRight style={{ fontSize: '18px' }} />
                </Button>
              </div>
            </div>
          )}

          {tabKey === 2 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              width: '100%',
              gap: '1rem'
            }}>
              <Button 
                variant="outline-secondary" 
                onClick={() => setTabKey(1)}
                style={{
                  borderRadius: '50px',
                  padding: '12px 24px',
                  fontWeight: 'bold',
                  minWidth: '100px',
                  transition: 'all 0.3s ease'
                }}
              >
                <SlArrowLeft style={{ marginRight: '8px' }} />
                {t('back') || 'ย้อนกลับ'}
              </Button>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button 
                  variant="outline-danger" 
                  onClick={() => setTabKey(1)}
                  style={{
                    borderRadius: '50px',
                    padding: '12px 24px',
                    fontWeight: 'bold',
                    minWidth: '100px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {t('cancel') || 'ยกเลิก'}
                </Button>

                {isFormUpdated && (
                  <Button 
                    className="btn-light-green" 
                    type="submit"
                    style={{
                      borderRadius: '50px',
                      padding: '12px 32px',
                      fontWeight: 'bold',
                      minWidth: '120px',
                      boxShadow: '0 4px 12px rgba(162, 177, 98, 0.4)',
                      transition: 'all 0.3s ease',
                      fontSize: '16px'
                    }}
                  >
                    {t('submit') || 'ยืนยัน'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Responsive CSS */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @media (max-width: 768px) {
              .modal-footer {
                padding: 1rem 1.5rem !important;
              }
              
              .modal-footer button {
                font-size: 14px !important;
                padding: 12px 24px !important;
                min-width: 100px !important;
              }
              
              .modal-footer div:first-child > div:first-child {
                font-size: 14px !important;
              }
            }
            
            @media (max-width: 576px) {
              .modal-footer {
                padding: 0.75rem 1rem !important;
              }
              
              .modal-footer button {
                font-size: 12px !important;
                padding: 10px 18px !important;
                min-width: 80px !important;
              }
              
              .modal-footer div:first-child > div:first-child {
                font-size: 12px !important;
              }
              
              /* Keep horizontal layout but make buttons smaller */
              .modal-footer > div > div:last-child {
                gap: 0.5rem !important;
              }
            }
            
            @media (max-width: 480px) {
              .modal-footer {
                padding: 0.5rem 0.75rem !important;
              }
              
              .modal-footer button {
                font-size: 11px !important;
                padding: 8px 16px !important;
                min-width: 70px !important;
                border-radius: 25px !important;
              }
              
              .modal-footer div:first-child > div:first-child {
                font-size: 11px !important;
              }
              
              /* Still horizontal but very compact */
              .modal-footer > div > div:last-child {
                gap: 0.25rem !important;
              }
              
              /* Hide text in back button on very small screens */
              .modal-footer button span {
                display: none !important;
              }
              
              .modal-footer button svg {
                margin: 0 !important;
              }
            }
            
            @media (max-width: 360px) {
              .modal-footer {
                padding: 0.5rem !important;
              }
              
              .modal-footer button {
                font-size: 10px !important;
                padding: 8px 12px !important;
                min-width: 60px !important;
                border-radius: 20px !important;
              }
              
              /* Tab 1: Stack vertically only on very tiny screens */
              .modal-footer > div:first-child {
                flex-direction: column !important;
                text-align: center !important;
                gap: 0.5rem !important;
              }
              
              .modal-footer > div:first-child > div:first-child {
                font-size: 10px !important;
                margin-bottom: 0.5rem !important;
              }
            }
          `
        }} />
      </Form>
    </div>
  );
};
